import type { PatientCalendarSelectionRequest } from "@medflyt/webapp-react/src/modules/patientProfileModal/patientProfileModal.actions";
import type { EntityProfileCalendarVisit } from "@medflyt/webapp-react/src/modules/entityProfileModal/sections/calendar/entityProfileCalendar.utils";
import { loadPatientVisitPrefill } from "@medflyt/webapp-react/src/modules/patientProfileModal/patient-visit-prefill";
import { createApi } from "@medflyt/webapp-react/src/core/api/api-utils";
import { EventFromReact } from "@medflyt/webapp-react/src/core/react-bridge";
import { LocalDate } from "@js-joda/core";
import ReactGlobalHost from "@medflyt/webapp-react/src/shared/components/ReactHost/ReactGlobalHost";
import ReactHost from "@medflyt/webapp-react/src/shared/components/ReactHost/ReactHost";
import { renderReactComponent } from "@medflyt/webapp-react/src/shared/utils/render-react-component";
import { queryClient, withAppOnly, withRouter } from "@medflyt/webapp-react/src/utils";
import angular from "angular";
import moment from "moment";
import {
  mapPatientPlansOfCareDutiesToFormDuties,
  NewPlanOfCareModalCtrl,
} from "../../scripts/controllers/newPlanOfCareModalCtrl";
import { DatabaseApiService } from "public/admin/scripts/services/db";
import { PlanOfCareTypeService } from "public/admin/scripts/services/planOfCareTypeService";
import React from "react";

const CAREGIVER_APPROVAL_STATUSES = [
  { id: 1, label: "Active", value: "ACTIVE", text: "Active", statusClass: "green" },
  { id: 2, label: "On Hold", value: "ON_HOLD", text: "On Hold", statusClass: "yellow" },
  { id: 3, label: "On Leave", value: "ON_LEAVE", text: "On Leave", statusClass: "orange" },
  {
    id: 4,
    label: "Pending Application",
    value: "PENDING",
    text: "Pending Application",
    statusClass: "lightblue",
  },
  { id: 5, label: "Inactive", value: "SUSPENDED", text: "Inactive", statusClass: "azur" },
  { id: 6, label: "Terminated", value: "TERMINATED", text: "Terminated", statusClass: "red" },
  { id: 7, label: "Quit", value: "QUIT", text: "Quit", statusClass: "azur" },
  { id: 8, label: "Rejected", value: "REJECTED", text: "Rejected", statusClass: "red" },
];

//! @ngInject
export class ReactAdapterService {
  private hasInitReactHost = false;
  private calendarRefreshCallbacks = new Set<() => void>();

  constructor(
    public $rootScope: angular.IRootScopeService,
    public Storage: any,
    public $state: ng.ui.IStateService,
    public $timeout: ng.ITimeoutService,
    public DatabaseApi: DatabaseApiService,
    public $http: angular.IHttpService,
    public $uibModal: ng.ui.bootstrap.IModalService,
    public entityNotesModalService: any,
    public exportProfileService: any,
    public createTaskModalService: any,
    public entityNewVisitModalService: any,
    public SweetAlert: any,
    public toaster: any,
    private planOfCareTypeService: PlanOfCareTypeService
  ) {
    this.listenForReactEvents();
    this.$rootScope.$on("refresh_visits", () => {
      window.dispatchEvent(new CustomEvent("medflyt:refresh_visits"));
      this.calendarRefreshCallbacks.forEach((callback) => callback());
      this.calendarRefreshCallbacks.clear();
    });
  }

  private mapPatientCalendarVisitForAngular(visit: any) {
    const startTime = moment(String(visit.startTime));
    const endTime = moment(String(visit.endTime));
    const visitBroadcast =
      visit.visitBroadcast?.visitBroadcastId === null ||
      visit.visitBroadcast?.visitBroadcastId === undefined
        ? null
        : visit.visitBroadcast;

    return {
      key: `visit-${visit.id}`,
      visitInstanceId: visit.id,
      day: startTime.format("dddd"),
      date: startTime.format("MM.DD.YYYY"),
      startTime,
      endTime,
      type:
        visit.status === "deleted"
          ? "DELETED_VISIT"
          : visit.status === "missed"
          ? "MISSED_VISIT"
          : visitBroadcast !== null
          ? "BROADCASTED_VISIT"
          : visit.caregiverId === null || visit.caregiverId === undefined
          ? "UNSTAFFED_VISIT"
          : visit.status === "task"
          ? "ASSIGNED_TASK"
          : "ASSIGNED_VISIT",
      visitBatchId: visit.visitBatchId,
      visitBatchType: visit.visitBatchType,
      visitBroadcast,
      visitBatchMinStartDate: visit.visitBatchMinStartDate,
      visitBatchMaxEndDate: visit.visitBatchMaxEndDate,
      caregiver: visit.caregiverId == null ? null : { id: visit.caregiverId },
      isBilled: visit.isBilled ?? (visit.billedSeconds ?? 0) > 0,
      isPaid: visit.isPaid ?? false,
      isOnPayrollDraft: visit.isOnPayrollDraft ?? (visit.payrollDraftSeconds ?? 0) > 0,
    };
  }

  private setPatientCalendarSelection(
    event: Extract<EventFromReact, { type: "patientProfileAction" }>
  ) {
    const visits = (event.payload.selectedVisits ?? []).map((visit) =>
      this.mapPatientCalendarVisitForAngular(visit)
    );

    this.entityNewVisitModalService.setSelectedItems({ visits, vacations: [] });

    return visits;
  }

  private setPatientCalendarEditData(
    event: Extract<EventFromReact, { type: "patientProfileAction" }>,
    prefill: Awaited<ReturnType<typeof loadPatientVisitPrefill>>
  ) {
    const patient = JSON.parse(JSON.stringify(event.payload.patient ?? { id: event.payload.patientId }));

    this.entityNewVisitModalService.setEditVisitData({
      patientId: event.payload.patientId,
      patient,
      patientStaffingPreferences: prefill.patientStaffingPreferences,
      patientAddress: patient.address,
      patientContracts: patient.contracts ?? [],
      patientMainLanguage: patient.mainLanguage,
      patientSecondaryLanguage: patient.secondaryLanguage,
      patientPhoneNumbers: patient.phoneNumbers ?? [],
      patientVacations: (patient.vacations ?? []).filter((vacation: any) => !vacation.removedAt),
      patientAuthorizations: prefill.patientAuthorizations,
      targetElementId: event.payload.targetElementId ?? "scroll-calendar",
      editShiftsParams: event.payload.calendarSelection?.type ?? "CURRENT_SHIFTS",
      editShiftsParamsUntilDate: event.payload.calendarSelection?.untilDate,
    });
  }

  private openReactPatientCalendarSidebar(
    state: string,
    event: Extract<EventFromReact, { type: "patientProfileAction" }>
  ) {
    if (event.payload.onDone !== undefined) {
      this.calendarRefreshCallbacks.add(event.payload.onDone);
    }

    this.$rootScope.$evalAsync(() => {
      this.$rootScope.$broadcast("react_patient_modal_open_side_bar", {
        state,
        patient: event.payload.patient,
      });
    });
  }

  private openPatientContractEditModal(
    event: Extract<EventFromReact, { type: "patientProfileAction" }>
  ) {
    const patientId = event.payload.patientId as any;
    const patient = (event.payload.patient ?? { id: patientId }) as any;
    const contract = event.payload.data as any;
    const scope = this.$rootScope.$new() as any;

    scope.patient = patient;
    scope.contract = contract;
    scope.showEditContract = true;
    scope.radioOptions = [
      { value: true, text: "Yes" },
      { value: false, text: "No" },
    ];
    scope.showIsOnHold = (item: any, fallback: string) => {
      const selected = scope.radioOptions.find((option: any) => option.value === item.isOnHold);
      return selected?.text ?? fallback;
    };
    scope.showIsPrimary = (item: any, fallback: string) => {
      const selected = scope.radioOptions.find((option: any) => option.value === item.isPrimary);
      return selected?.text ?? fallback;
    };
    scope.validateMedicaidNumber = () => true;
    scope.patchContract = (item: any, field: string) => {
      if (
        (field === "startDate" || field === "endDate") &&
        item.startDate &&
        item.endDate &&
        moment(item.endDate).isBefore(moment(item.startDate))
      ) {
        this.toaster.pop("error", "End date can't be before start date");
        return;
      }

      const body = { id: item.id, [field]: item[field] };

      return this.DatabaseApi.patch(
        `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
          (this.$rootScope as any).agencyMemberId
        }/patients/${patientId}/contracts/${item.id}`,
        body
      ).then(
        () => {
          this.toaster.pop("success", "Success", "Contract changed successfully");
          event.payload.onDone?.();
        },
        () => {
          this.toaster.pop("error", "Something went wrong", "could not edit contract");
        }
      );
    };
    scope.uploadContractFile = ($event: Event, item: any) => {
      $event.stopPropagation();
      this.uploadPatientContractFile(event, item);
    };

    const modal = this.$uibModal.open({
      template: `
        <div class="modal-header">
          <button type="button" class="close" ng-click="$dismiss()">&times;</button>
          <h3 class="modal-title">Edit patient contract</h3>
        </div>
        <div class="modal-body">
          <ng-include src="'admin/views/patient-contract.html'"></ng-include>
        </div>
      `,
      scope,
      size: "lg",
      windowClass: "modal center-center",
    });

    return modal.result.finally(() => event.payload.onDone?.()).catch(() => undefined);
  }

  private uploadPatientContractFile(
    event: Extract<EventFromReact, { type: "patientProfileAction" }>,
    contractOverride?: any
  ) {
    const contract = contractOverride ?? (event.payload.data as any);

    if (contract === null || contract === undefined) {
      return undefined;
    }

    contract.title = contract.contractTypeName;
    contract.documentTypeId = contract.id;

    return (this.$rootScope as any).openUploadDocumentPdfModal({
      url: `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
        (this.$rootScope as any).agencyMemberId
      }/patient_contract/upload_file`,
      document: contract,
      successCallback: (result: any) => {
        if (result !== null) {
          if (contract.files === undefined) {
            contract.files = [];
          }
          contract.files.push(result.data.url);
          event.payload.onDone?.();
        }
      },
    });
  }

  private uploadAuthorizationFile(
    event: Extract<EventFromReact, { type: "patientProfileAction" }>
  ) {
    const authorization = event.payload.data as any;

    if (authorization === null || authorization === undefined) {
      return undefined;
    }

    authorization.title = `${authorization.contractName ?? "Patient"} authorization`;
    authorization.documentTypeId = authorization.id;

    return (this.$rootScope as any).openUploadDocumentPdfModal({
      url: `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
        (this.$rootScope as any).agencyMemberId
      }/patient_authorization/upload_file`,
      document: authorization,
      successCallback: (result: any) => {
        if (result !== null) {
          if (authorization.files === undefined) {
            authorization.files = [];
          }
          authorization.files.push(result.data);
          event.payload.onDone?.();
        }
      },
    });
  }

  private openAuthorizationFile(event: Extract<EventFromReact, { type: "patientProfileAction" }>) {
    const data = event.payload.data as any;
    const fileId = data?.fileId ?? data?.id;

    if (fileId === null || fileId === undefined) {
      return undefined;
    }

    return this.DatabaseApi.get(
      `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
        (this.$rootScope as any).agencyMemberId
      }/patient_authorization_file/${fileId}`
    ).then(
      (res: any) => window.open(res.data.url),
      () => this.toaster.pop("error", "Something went wrong", "Can't get authorization file")
    );
  }

  private deleteAuthorization(event: Extract<EventFromReact, { type: "patientProfileAction" }>) {
    const authorization = event.payload.data as any;
    const authorizationId = authorization?.id ?? authorization;

    if (authorizationId === null || authorizationId === undefined) {
      return undefined;
    }

    return new Promise<void>((resolve) => {
      this.SweetAlert.swal(
        {
          title: "Delete Authorization",
          text: "Deleting this authorization will remove all allocated hours from visits.",
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3077EB",
          confirmButtonText: "Confirm",
          closeOnConfirm: true,
          closeOnCancel: true,
        },
        (isConfirm: boolean) => {
          if (!isConfirm) {
            resolve();
            return;
          }

          (this.DatabaseApi as any).delete(
            `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
              (this.$rootScope as any).agencyMemberId
            }/authorization/${authorizationId}`
          )
            .then(() => {
              this.toaster.pop("success", "Successfully deleted authorization");
              event.payload.onDone?.();
            })
            .catch((err: any) => {
              if (!err.data?.totalVisits) {
                this.toaster.pop("error", "Something went wrong", "Could not delete authorization");
                return;
              }

              const totalVisits = `Authorization has ${err.data.totalVisits} ${
                err.data.totalVisits === 1 ? "visit" : "visits"
              }`;
              const authorizationVisitsRange =
                err.data.totalVisits === 1
                  ? `at: ${moment(err.data.firstVisitDate).format("MM/DD/YYYY")}`
                  : `from ${moment(err.data.firstVisitDate).format("MM/DD/YYYY")} - ${moment(
                      err.data.lastVisitDate
                    ).format("MM/DD/YYYY")}`;
              const billedOrPaid = `that ${
                err.data.totalVisits === 1 ? "is" : "are"
              } billed or paid.`;

              this.SweetAlert.swal({
                title: "Can't delete authorization",
                text: `${totalVisits} ${authorizationVisitsRange} ${billedOrPaid}`,
                type: "error",
                confirmButtonColor: "#3077EB",
              });
            })
            .finally(resolve);
        }
      );
    });
  }

  private autoAllocateAuthorization(
    event: Extract<EventFromReact, { type: "patientProfileAction" }>
  ) {
    const data = event.payload.data as any;
    const authorization = data?.authorization;
    const isFullAllocation = data?.full === true;

    if (authorization === null || authorization === undefined) {
      return undefined;
    }

    const fullMessage = isFullAllocation ? "" : "(that haven't been billed) ";

    return new Promise<void>((resolve) => {
      this.SweetAlert.swal(
        {
          title: "Auto Allocate Authorization?",
          text:
            "This will clear all the visit allocations " +
            fullMessage +
            "and recalculate them optimally (using all available Authorizations)",
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3077EB",
          confirmButtonText: "Yes, Auto Allocate",
          closeOnConfirm: true,
          closeOnCancel: true,
        },
        (isConfirm: boolean) => {
          if (!isConfirm) {
            resolve();
            return;
          }

          this.DatabaseApi.post(
            `agencies/${(this.$rootScope as any).agencyId}/auto_recalculate_visit_authorizations`,
            {
              patientContractId: authorization.patientContractId,
              serviceCodeId: authorization.serviceCode,
              full: isFullAllocation,
            }
          )
            .then(() => {
              this.toaster.pop("success", "Success", "Auto Recalculated Authorizations");
              this.$rootScope.$emit("refresh_visits");
              event.payload.onDone?.();
            })
            .catch((err: any) => {
              const errorText = err.data?.error ?? "";
              this.toaster.pop("error", "Something went wrong", errorText);
            })
            .finally(resolve);
        }
      );
    });
  }

  private clickLegacyCaregiverNavigationItem(selector: string, label: string) {
    const items = Array.from(document.querySelectorAll<HTMLElement>(selector));
    const item = items.find((element) => element.textContent?.trim() === label);

    item?.click();

    return item !== undefined;
  }

  private activateLegacyCaregiverSection(
    event: Extract<EventFromReact, { type: "caregiverProfileAction" }>
  ) {
    const sectionLabel = event.payload.legacySectionLabel;
    const subsectionLabel = event.payload.legacySubsectionLabel;

    if (sectionLabel === undefined || subsectionLabel === undefined) {
      return;
    }

    const activate = () => {
      const didClickSection = this.clickLegacyCaregiverNavigationItem(
        ".caregiver-modal .caregiver-main-header-navigation-primary li",
        sectionLabel
      );

      if (!didClickSection) {
        return;
      }

      this.$timeout(
        () =>
          this.clickLegacyCaregiverNavigationItem(
            ".caregiver-modal .caregiver-main-header-navigation-secondary li",
            subsectionLabel
          ),
        0,
        false
      );
    };

    [0, 250, 750].forEach((delay) => this.$timeout(activate, delay, false));
  }

  init() {
    if (!this.hasInitReactHost) {
      renderReactComponent({
        $element: [document.body],
        component: withAppOnly(withRouter(React.memo(ReactHost))),
        props: {},
      });

      renderReactComponent({
        $element: [document.body],
        component: withAppOnly(withRouter(React.memo(ReactGlobalHost))),
        props: {},
      });

      this.hasInitReactHost = true;
    }
  }

  /**
   * The Pending Acceptance page used to own these two actions. The page is gone -
   * the React caregivers list shows join requests via its status filter - but the
   * approve modal it opened is unchanged, so both actions are driven from here.
   */
  private handleCaregiverJoinRequest(
    payload: { action: "approve" | "remove"; caregiverId: number },
    onDone?: () => void
  ) {
    if (payload.action === "approve") {
      const modalInstance = this.$uibModal.open({
        templateUrl: "admin/views/status-modal.html",
        size: "md",
        controller: "approveCaregiverModalCtrl",
        resolve: {
          // The same statuses the Pending Acceptance page offered.
          statuses: () => CAREGIVER_APPROVAL_STATUSES,
          caregiver: () => ({ id: payload.caregiverId, status: "JOIN_REQUEST" }),
          certifications: () => (this.DatabaseApi as any).activeAgencyCertifications()
        }
      });

      modalInstance.result.then(
        () => {
          (this.DatabaseApi as any).reloadPendingCaregivers();
          onDone?.();
        },
        () => undefined
      );
      return;
    }

    this.SweetAlert.swal(
      {
        title: "Remove Request?",
        text: "",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#DD6B55",
        confirmButtonText: "Yes, Remove",
        closeOnConfirm: true,
        closeOnCancel: true
      },
      (isConfirm: boolean) => {
        if (!isConfirm) return;

        (this.DatabaseApi as any).delete(
          `agencies/${this.$rootScope.agencyId}/caregivers/${payload.caregiverId}/remove_join_request`
        ).then(
          () => {
            this.toaster.pop("success", "Join request removed");
            (this.DatabaseApi as any).reloadPendingCaregivers();
            onDone?.();
          },
          () => this.toaster.pop("error", "Something went wrong", "Could not remove the request")
        );
      }
    );
  }

  listenForReactEvents() {
    window.addEventListener("from-webapp-react", ($event) => {
      const event = ($event as CustomEvent).detail as EventFromReact;
      if (event.type === "caregiverJoinRequest") {
        return this.handleCaregiverJoinRequest(event.payload, event.onDone);
      }
      if (event.type === "navigate") {
        switch (event.payload.entity) {
          case "Caregiver":
            return this.$rootScope.openCaregiverModal(
              event.payload.id as any,
              undefined,
              undefined,
              undefined,
              {
                redirect: event.redirect,
                onClose: event.onClose,
              }
            );
          case "Patient":
            return this.$rootScope.openPatientModal(event.payload.id as any, undefined, {
              redirect: event.redirect,
              onClose: event.onClose,
            });
          case "VisitInstance":
            return this.$rootScope.openVisitInstanceModal(event.payload.id as any, undefined, {
              redirect: event.redirect,
              onClose: event.onClose,
            });
          case "VisitBroadcast":
            return this.$rootScope.openVisitBroadcastModalById(event.payload.id as any, {
              onClose: event.onClose,
            });
          case "Document":
            return this.$rootScope.openDocumentPreviewModalById(event.payload.id as any);
        }
      } else if (event.type === "patientProfileAction") {
        const patientId = event.payload.patientId as any;
        const patientAction = event.payload.action as string;

        switch (patientAction) {
          case "openChat":
            if ((this.$rootScope as any).isOnboardingAgency) {
              return this.$rootScope.react.commCenter.openEntityCommunications({
                entity: "Patient",
                id: +patientId as any,
              });
            }

            this.$rootScope.$evalAsync(() => {
              this.$rootScope.$broadcast("react_patient_modal_open_side_bar", {
                state: "CHATS",
                patient: event.payload.patient,
              });
            });

            return undefined;
          case "openNotes":
            return this.entityNotesModalService.handleNotesModalOpen({
              profileId: patientId,
              profileName: event.payload.patientName,
              notesType: "Patient",
            });
          case "openIntakeModal":
            return this.$state.go("app.patients.intake.dashboard.profile", { patientId });
          case "exportProfile":
            return this.exportProfileService
              .exportPatientProfilePDF(
                (this.$rootScope as any).agencyId,
                (this.$rootScope as any).agencyMemberId,
                patientId
              )
              .finally(() => event.payload.onDone?.());
          case "openExportProfileSettings":
            return this.$uibModal.open({
              templateUrl: "admin/views/export-profile-modal.html",
              size: "md",
              controller: "exportPatientProfileModal",
              resolve: {
                agencyId: () => (this.$rootScope as any).agencyId,
                agencyMemberId: () => (this.$rootScope as any).agencyMemberId,
                patientId: () => patientId,
              },
            });
          case "openCertificationPeriod": {
            const patient = (event.payload.patient ?? { id: patientId }) as any;
            const actionData = (event.payload.data ?? {}) as any;
            const certificationPeriod =
              actionData.certificationPeriod ??
              (patient.startOfCare === null || patient.startOfCare === undefined
                ? { type: "START_OF_CARE" }
                : {
                    type: "START_OF_CARE",
                    startDate: new Date(`${patient.startOfCare}T00:00:00.000Z`),
                  });

            const modal = this.$uibModal.open({
              templateUrl: "admin/views/certification-period-modal.html",
              size: "md",
              controller: "certificationPeriodModal",
              windowClass: "modal center-center",
              resolve: {
                patientId: () => patientId,
                allCertificationPeriods: () => actionData.allCertificationPeriods ?? [],
                certificationPeriod: () => certificationPeriod,
                patientStartOfCareDate: () => patient.startOfCare ?? null,
                onSubmitted: () => () => event.payload.onDone?.(),
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openCertificationPeriods": {
            const patient = (event.payload.patient ?? { id: patientId }) as any;
            const scope = this.$rootScope.$new() as any;
            scope.patientId = patientId;
            scope.patientStartOfCareDate = patient.startOfCare ?? null;
            scope.onPatientSocUpdate = (newVal: string) => {
              patient.startOfCare = newVal;
              event.payload.onDone?.();
            };

            const modal = this.$uibModal.open({
              template: `
                <div class="modal-header">
                  <h2 class="modal-title">Certification periods</h2>
                </div>
                <div class="modal-body">
                  <patient-medical-summary
                    patient-id="patientId"
                    patient-start-of-care-date="patientStartOfCareDate"
                    on-patient-soc-update="onPatientSocUpdate(newVal)"
                  ></patient-medical-summary>
                </div>
              `,
              size: "lg",
              scope,
              windowClass: "modal center-center",
            });

            return modal.result.finally(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openPatientContract": {
            if (event.payload.data !== undefined && event.payload.data !== null) {
              return this.openPatientContractEditModal(event);
            }

            const modal = this.$uibModal.open({
              templateUrl: "admin/views/patient-contract-modal.html",
              size: "md",
              controller: "patientContractModalCtrl",
              resolve: {
                patient: () => event.payload.patient ?? { id: patientId, contracts: [] },
                codes: () => (this.DatabaseApi as any).activeServiceCodes(),
                contracts: () => (this.DatabaseApi as any).contractTypes(),
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "uploadPatientContractFile":
            return this.uploadPatientContractFile(event);
          case "openAuthorization": {
            const scope = this.$rootScope.$new() as any;
            scope.patientContracts = ((event.payload.patient as any)?.contracts ?? []).map(
              (contract: any) => ({
                ...contract,
                contractId: contract.contractId ?? contract.id,
              })
            );
            scope.getAuthorizations = () => event.payload.onDone?.();
            scope.periodTypesOptions = {
              DAILY: "Daily",
              WEEKLY: "Weekly",
              MONTHLY: "Monthly",
              ENTIRE_PERIOD: "Entire period",
            };
            scope.firstDayOfWeekOptions = {
              SUNDAY: "Sunday",
              MONDAY: "Monday",
              TUESDAY: "Tuesday",
              WEDNESDAY: "Wednesday",
              THURSDAY: "Thursday",
              FRIDAY: "Friday",
              SATURDAY: "Saturday",
            };
            scope.lastAuthorization = undefined;

            const modal = this.$uibModal.open({
              templateUrl: "admin/views/authorization-setup-modal.html",
              controller: "authorizationSetupModalCtrl",
              scope,
              size: "lg",
              resolve: {
                fields: () => (this.DatabaseApi as any).formFields("Authorization"),
                data: () => event.payload.data ?? {},
                serviceCodes: () => (this.DatabaseApi as any).activeServiceCodes(),
                patientId: () => patientId,
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openAuthorizationFile":
            return this.openAuthorizationFile(event);
          case "uploadAuthorizationFile":
            return this.uploadAuthorizationFile(event);
          case "openAuthorizationUtilization":
            return this.$uibModal.open({
              templateUrl: "admin/views/authorization-utilization-modal.html",
              controller: "authorizationUtilizationModalCtrl",
              scope: this.$rootScope.$new(),
              size: "lg",
              resolve: {
                authorization: () => event.payload.data,
              },
            });
          case "deleteAuthorization":
            return this.deleteAuthorization(event);
          case "autoAllocateAuthorization":
            return this.autoAllocateAuthorization(event);
          case "openPatientBillingInvoices": {
            const scope = this.$rootScope.$new() as any;
            scope.patientId = patientId;

            const modal = this.$uibModal.open({
              template: `
                <div class="modal-header">
                  <button type="button" class="close" ng-click="$close()">&times;</button>
                  <h2 class="modal-title">Billing invoices</h2>
                </div>
                <div class="modal-body">
                  <patient-billing patient-id="patientId"></patient-billing>
                </div>
              `,
              scope,
              size: "lg",
              windowClass: "modal center-center react-patient-billing-invoices-modal",
            });

            return modal.result.finally(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openPatientBillingInvoiceDetails": {
            const invoiceId = (event.payload.data as any)?.invoiceId;

            if (invoiceId === null || invoiceId === undefined) {
              return undefined;
            }

            return (this.$rootScope as any).openInvoiceModal({ invoiceId });
          }
          case "openPatientBillingInvoiceExportHistory": {
            const visit = (event.payload.data as any)?.visit;

            if (visit === null || visit === undefined) {
              return undefined;
            }

            return this.$uibModal.open({
              templateUrl: "admin/views/billing-exported-invoices-modal.html",
              size: "md",
              controller: "billingExportedInvoicesModalCtrl",
              windowClass: "billing-exported-invoices-modal-ctrl center-center",
              resolve: {
                visit: () => visit,
              },
            });
          }
          case "openPatientAdministrativeDiagnosisCodes": {
            const scope = this.$rootScope.$new() as any;
            scope.patient = event.payload.patient ?? { id: patientId, contracts: [] };

            const modal = this.$uibModal.open({
              template: `
                <div class="modal-header">
                  <button type="button" class="close" ng-click="$close()">&times;</button>
                  <h2 class="modal-title">Diagnosis codes</h2>
                </div>
                <div class="modal-body">
                  <patient-diagnosis-codes
                    patient="patient"
                    readonly="false"
                  ></patient-diagnosis-codes>
                </div>
              `,
              scope,
              size: "lg",
              windowClass: "modal center-center react-patient-diagnosis-codes-modal",
            });

            return modal.result.finally(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openPatientAdministrativeDiagnosisCode": {
            const scope = this.$rootScope.$new() as any;
            scope.patient = event.payload.patient ?? { id: patientId, contracts: [] };
            scope.patientId = patientId;
            scope.getPatientContractDiagnosisCode = () => event.payload.onDone?.();

            const modal = this.$uibModal.open({
              templateUrl: "admin/views/patient-contract-diagnosis-code-setup-modal.html",
              controller: "patientContractDiagnosisCodeSetupModalCtrl",
              scope,
              size: "lg",
              resolve: {
                isAnswer: () => false,
                data: () => event.payload.data,
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openPatientPayRates": {
            const scope = this.$rootScope.$new() as any;
            scope.patientId = patientId;

            const modal = this.$uibModal.open({
              template: `
                <div class="modal-header">
                  <button type="button" class="close" ng-click="$close()">&times;</button>
                  <h2 class="modal-title">Pay rates</h2>
                </div>
                <div class="modal-body">
                  <patient-pay-rates patient-id="patientId"></patient-pay-rates>
                </div>
              `,
              scope,
              size: "lg",
              windowClass: "modal center-center react-patient-pay-rates-modal",
            });

            return modal.result.finally(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openPatientPayRate": {
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/patient-pay-rate-modal.html",
              size: "lg",
              controller: "patientPayRateModalCtrl",
              resolve: {
                patientId: () => patientId,
                patientPayRateInitValues: () => event.payload.data,
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openPatientPayRateReset": {
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/patient-pay-rate-reset-modal.html",
              size: "lg",
              controller: "patientPayRateResetModalCtrl",
              resolve: {
                patientId: () => patientId,
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openPatientPhysician": {
            const patientPhysicians = ((event.payload.data as any)?.patientPhysicians ??
              []) as any[];
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/new-patient-physician-modal.html",
              controller: "newPatientPhysicianModalCtrl",
              size: "md",
              resolve: {
                patientId: () => patientId,
                patientPhysicians: () => patientPhysicians,
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "deletePatientPhysician": {
            const patientPhysician = event.payload.data as any;
            const patientPhysicianId = patientPhysician?.id ?? patientPhysician;

            if (patientPhysicianId === null || patientPhysicianId === undefined) {
              return undefined;
            }

            return new Promise<void>((resolve) => {
              this.SweetAlert.swal(
                {
                  title: "Remove Patient Physician",
                  text: "Are you sure you want to remove this physician for the current patient?",
                  type: "warning",
                  showCancelButton: true,
                  confirmButtonColor: "#3898ff",
                  confirmButtonText: "Yes, remove",
                  closeOnConfirm: true,
                  closeOnCancel: true,
                },
                (hasConfirmed: boolean) => {
                  if (!hasConfirmed) {
                    resolve();
                    return;
                  }

                  (this.DatabaseApi as any)
                    .delete(
                      `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
                        (this.$rootScope as any).agencyMemberId
                      }/patients/${patientId}/patient_physicians/${patientPhysicianId}`
                    )
                    .then(() => {
                      this.toaster.pop("success", "Patient physician removed successfully");
                      event.payload.onDone?.();
                    })
                    .catch(() =>
                      this.toaster.pop(
                        "error",
                        "Something went wrong",
                        "Failed to remove patient physician"
                      )
                    )
                    .finally(resolve);
                }
              );
            });
          }
          case "updatePatientPhysicianPrimary": {
            const patientPhysician = event.payload.data as any;
            const patientPhysicianId = patientPhysician?.id;

            if (patientPhysicianId === null || patientPhysicianId === undefined) {
              return undefined;
            }

            return (this.DatabaseApi as any)
              .patch(
                `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
                  (this.$rootScope as any).agencyMemberId
                }/patients/${patientId}/patient_physicians/${patientPhysicianId}`,
                { isPrimary: patientPhysician.isPrimary === true }
              )
              .then(() => {
                this.toaster.pop("success", "Patient physician updated successfully");
                event.payload.onDone?.();
              })
              .catch(() =>
                this.toaster.pop(
                  "error",
                  "Something went wrong",
                  "Failed to update patient physician"
                )
              );
          }
          case "openPatientDocument": {
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/patient-main-documents-new-document-modal.html",
              controller: "patientMainDocumentsNewDocumentModalCtrl",
              size: "md",
              windowClass: "center-center",
              resolve: {
                documentTypes: () => (this.DatabaseApi as any).patientDocumentTypes(),
                patientId: () => patientId,
                patientName: () => event.payload.patientName,
                onDocumentCreated: () => () => event.payload.onDone?.(),
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openPatientMedicationProfileLegacy":
            return this.$rootScope.openPatientModal(-Number(patientId) as any, undefined, {
              redirect: false,
              onClose: event.payload.onDone,
            });
          case "deletePatientMedicationProfile": {
            const medication = event.payload.data as any;

            if (medication?.id === null || medication?.id === undefined) {
              return undefined;
            }

            return new Promise<void>((resolve) => {
              this.SweetAlert.swal(
                {
                  title: "Remove?",
                  text: "Are you sure you want to remove this medication?",
                  type: "warning",
                  showCancelButton: true,
                  confirmButtonColor: "#3077EB",
                  confirmButtonText: "Yes, remove",
                  closeOnConfirm: true,
                  closeOnCancel: true,
                },
                (hasConfirmed: boolean) => {
                  if (!hasConfirmed) {
                    resolve();
                    return;
                  }

                  (this.DatabaseApi as any)
                    .put(
                      `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
                        (this.$rootScope as any).agencyMemberId
                      }/patient/${patientId}/medication_profile`,
                      { ...medication, status: "DISCONTINUED" }
                    )
                    .then(() => {
                      this.toaster.pop("success", "Medication removed successfully");
                      event.payload.onDone?.();
                    })
                    .catch(() =>
                      this.toaster.pop(
                        "error",
                        "Something went wrong",
                        "Could not remove medication"
                      )
                    )
                    .finally(resolve);
                }
              );
            });
          }
          case "openPatientSurpluses": {
            const scope = this.$rootScope.$new() as any;
            scope.patientId = patientId;

            const modal = this.$uibModal.open({
              template: `
                <div class="modal-header">
                  <button type="button" class="close" ng-click="$close()">&times;</button>
                  <h2 class="modal-title">Surpluses</h2>
                </div>
                <div class="modal-body">
                  <section class="wrapper-content">
                    <patient-surplus-list patient-id="patientId"></patient-surplus-list>
                  </section>
                  <section class="wrapper-content">
                    <patient-surplus-invoice-list patient-id="patientId"></patient-surplus-invoice-list>
                  </section>
                  <section class="wrapper-content">
                    <patient-surplus-checks-list patient-id="patientId"></patient-surplus-checks-list>
                  </section>
                </div>
              `,
              scope,
              size: "lg",
              windowClass: "modal center-center react-patient-surpluses-modal",
            });

            return modal.result.finally(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openPatientMeasurement": {
            const scope = this.$rootScope.$new() as any;
            scope.patientId = patientId;
            scope.getPatientVitals = () => event.payload.onDone?.();

            const openModal = () => {
              const modal = this.$uibModal.open({
                templateUrl: "admin/views/patient-measurement-modal.html",
                controller: "patientMeasurementModalCtrl",
                scope,
              });

              return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
            };

            return this.DatabaseApi.get(
              `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
                (this.$rootScope as any).agencyMemberId
              }/patients/${patientId}/nursing_questions`
            )
              .then((res: any) => {
                scope.vitalQuestions = (res.data.questions ?? []).filter(
                  (question: any) => question.isVital
                );
                return openModal();
              })
              .catch(() => {
                this.toaster.pop("error", "Something went wrong", "Failed to load vital questions");
              });
          }
          case "openPatientVbpReport": {
            const patient = (event.payload.patient ?? { id: patientId }) as any;
            const actionData = (event.payload.data ?? {}) as any;
            const openModal = (questions: any[]) =>
              this.$uibModal
                .open({
                  templateUrl: "admin/views/patient-vbp-new-report-modal.html",
                  size: "md",
                  controller: "patientVbpNewReportModalCtrl",
                  resolve: {
                    patient: () => patient,
                    questions: () => questions,
                  },
                })
                .result.then((result: unknown) => {
                  if (result === "success") {
                    event.payload.onDone?.();
                  }
                })
                .catch(() => undefined);

            if (Array.isArray(actionData.questions)) {
              return openModal(actionData.questions);
            }

            return this.DatabaseApi.get(
              `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
                (this.$rootScope as any).agencyMemberId
              }/patients/${patientId}/nursing_questions`
            )
              .then((res: any) =>
                openModal((res.data.questions ?? []).filter((question: any) => question.isVBP))
              )
              .catch(() => {
                this.toaster.pop("error", "Something went wrong", "Failed to load VBP questions");
              });
          }
          case "openLegacyPatientMedicalSection":
            return this.$rootScope.openPatientModal(-Number(patientId) as any, undefined, {
              redirect: false,
              onClose: event.payload.onDone,
            });
          case "openPatientPlanOfCare": {
            const patient = (event.payload.patient ?? { id: patientId }) as any;
            const selectedPlanOfCare = (event.payload.data as any)?.selectedPlanOfCare;
            const endDate = selectedPlanOfCare?.endDate;
            const nextStartDate =
              endDate === null || endDate === undefined
                ? undefined
                : typeof endDate === "string"
                ? LocalDate.parse(endDate).plusDays(1)
                : endDate.plusDays(1);

            if (patient.currentOfficeId === null || patient.currentOfficeId === undefined) {
              this.toaster.pop("warning", "Missing patient office", "Can't create plan of care");
              return undefined;
            }

            return this.$uibModal
              .open({
                templateUrl: "admin/views/new-plan-of-care-modal.html",
                size: "lg",
                controllerAs: "ctrl",
                controller: NewPlanOfCareModalCtrl.$name,
                resolve: {
                  initialFormData: () => ({
                    startDate: nextStartDate,
                    duties: mapPatientPlansOfCareDutiesToFormDuties(
                      selectedPlanOfCare?.duties ?? []
                    ),
                    selectedPlanOfCare,
                  }),
                  patientId: () => patientId,
                  patientOfficeId: () => patient.currentOfficeId,
                },
              })
              .result.then((result: unknown) => {
                if (result === "success") {
                  event.payload.onDone?.();
                  this.$rootScope.$emit("patient_poc_updated");
                }
              })
              .catch(() => undefined);
          }
          case "openPatientPlanOfCareHistory":
            return this.$rootScope.openPatientPocHistoryModal(
              event.payload.patient ?? { id: patientId }
            );
          case "openPatientDutySheetExport": {
            const actionData = (event.payload.data ?? {}) as any;

            return this.$uibModal
              .open({
                templateUrl: "admin/views/export-duty-sheet-data-modal.html",
                controller: "exportDutySheetDataModalCtrl",
                controllerAs: "ctrl",
                size: "md",
                resolve: {
                  fromDate: () => actionData.fromDate,
                  patientId: () => patientId,
                  planOfCareId: () => actionData.planOfCareId,
                },
              })
              .result.then((result: unknown) => {
                if (result === "success") {
                  event.payload.onDone?.();
                }
              })
              .catch(() => undefined);
          }
          case "openLinkPatient": {
            const scope = this.$rootScope.$new() as any;
            scope.patient = event.payload.patient ?? { id: patientId };

            return this.$uibModal.open({
              templateUrl: "admin/views/linked-patient-setup-modal.html",
              controller: "linkedPatientSetupModalCtrl",
              scope,
              size: "md",
            });
          }
          case "removeLinkedPatient": {
            const patient = (event.payload.patient ?? { id: patientId }) as any;
            const linkedPatientId = patient.linkedPatient?.linkedWith;

            if (linkedPatientId === null || linkedPatientId === undefined) {
              return undefined;
            }

            return new Promise<void>((resolve) => {
              this.SweetAlert.swal(
                {
                  title: "Remove?",
                  text: "Are you sure you want to remove this link?",
                  type: "warning",
                  showCancelButton: true,
                  confirmButtonColor: "#3077EB",
                  confirmButtonText: "Yes, remove",
                  closeOnConfirm: true,
                  closeOnCancel: true,
                },
                (isConfirm: boolean) => {
                  if (!isConfirm) {
                    resolve();
                    return;
                  }

                  (this.DatabaseApi as any).delete(
                    `agencies/${
                      (this.$rootScope as any).agencyId
                    }/patients/${patientId}/linked_patient/${linkedPatientId}`
                  )
                    .then(() => {
                      this.toaster.pop("success", "Success", "Linked patient removed successfully");
                      event.payload.onDone?.();
                    })
                    .catch(() => {
                      this.toaster.pop(
                        "error",
                        "Something went wrong",
                        "Failed to remove linked patient"
                      );
                    })
                    .finally(resolve);
                }
              );
            });
          }
          case "openNewVisit": {
            const patient = JSON.parse(JSON.stringify(event.payload.patient ?? { id: patientId }));
            const api = createApi();
            return loadPatientVisitPrefill(queryClient, api, patientId).then((prefill) => {
              this.$rootScope.$evalAsync(() => {
                this.$rootScope.$broadcast("react_patient_modal_open_side_bar", {
                  state: "NEW_VISIT",
                  patient,
                  ...prefill,
                  calendarDate: event.payload.calendarDate === undefined
                    ? undefined
                    : new Date(`${event.payload.calendarDate}T00:00:00.000Z`),
                });
              });
            }).catch(() => {
              this.toaster.pop("error", "Could not open new visit", "Could not load patient visit data");
            });
          }
          case "openNewTask":
            return this.createTaskModalService.openNewPatientTaskModal({
              patient: event.payload.patient ?? { id: patientId },
              date:
                event.payload.calendarDate === undefined
                  ? undefined
                  : new Date(`${event.payload.calendarDate}T00:00:00.000Z`),
            });
          case "openPatientTask":
            return this.createTaskModalService
              .openNewPatientTaskModal({
                patient: event.payload.patient ?? { id: patientId },
                task: event.payload.task,
              })
              .then(() => event.payload.onDone?.())
              .catch(() => undefined);
          case "cancelPatientTask": {
            const scope = this.$rootScope.$new() as any;
            scope.task = event.payload.task;

            const modal = this.$uibModal.open({
              templateUrl: "admin/views/patient-upcoming-task-cancel-modal.html",
              size: "md",
              controller: "patientUpcomigTaskCancelModalCtrl",
              scope,
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "schedulePatientTask": {
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/patient-schedule-task-modal.html",
              controller: "patientScheduleTaskModalCtrl",
              size: "md",
              resolve: {
                task: () => event.payload.task,
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "selectPatientCalendarVisits": {
            const request = event.payload.data as PatientCalendarSelectionRequest;
            const seeds = (event.payload.selectedVisits ?? []) as EntityProfileCalendarVisit[];
            const angularVisits = seeds.map((visit) =>
              this.mapPatientCalendarVisitForAngular(visit)
            );
            return this.entityNewVisitModalService
              .fetchAllVisits(patientId, request.selection, angularVisits)
              .then(({ data }: any) => {
                const selected = data.visits.map((visit: any): EntityProfileCalendarVisit => {
                  const seed = seeds.find((item) => item.visitBatchId === visit.visit_batch_id);
                  return {
                    id: visit.visit_instance_id,
                    date: String(visit.start_time).slice(0, 10),
                    startTime: visit.start_time,
                    endTime: visit.end_time,
                    caregiverId: visit.caregiver_id,
                    patientId,
                    title: seed?.title ?? "",
                    status:
                      visit.removed_at != null
                        ? "deleted"
                        : visit.missed_visit
                        ? "missed"
                        : visit.is_task
                        ? "task"
                        : "visit",
                    visitBatchId: visit.visit_batch_id,
                    visitBatchType: visit.batch_type ?? seed?.visitBatchType,
                    addressTimezone: seed?.addressTimezone,
                    isBilled: visit.is_billed,
                    isPaid: visit.is_paid,
                    isOnPayrollDraft: visit.draft_paid_seconds > 0,
                    visitBroadcast:
                      visit.visit_broadcast_id == null
                        ? null
                        : {
                            visitBroadcastId: visit.visit_broadcast_id,
                            startDateTime: visit.start_time,
                          },
                  };
                });
                request.onSelected(selected);
              })
              .catch(request.onError);
          }
          case "editPatientCalendarItems": {
            return loadPatientVisitPrefill(queryClient, createApi(), patientId).then((prefill) => {
              this.setPatientCalendarSelection(event);
              this.setPatientCalendarEditData(event, prefill);
              this.openReactPatientCalendarSidebar("EDIT_NEW_VISIT", event);
            }).catch(() => {
              this.toaster.pop("error", "Could not open edit visit", "Could not load patient visit data");
            });
          }
          case "deletePatientCalendarItems": {
            const visits = this.setPatientCalendarSelection(event);
            this.entityNewVisitModalService.setDeleteVisitData({
              patientId,
              selectedVisits: visits,
              editShiftsParams: event.payload.calendarSelection ?? { type: "CURRENT_SHIFTS" },
            });
            this.openReactPatientCalendarSidebar("DELETE_VISIT", event);

            return undefined;
          }
          case "missPatientCalendarItems": {
            const visits = this.setPatientCalendarSelection(event);
            this.entityNewVisitModalService.setMissedVisitData({
              patientId,
              selectedVisits: visits,
              editShiftsParams: event.payload.calendarSelection ?? { type: "CURRENT_SHIFTS" },
            });
            this.openReactPatientCalendarSidebar("MISS_VISIT", event);

            return undefined;
          }
          case "approvePatientCalendarClockLocation": {
            const visits = event.payload.selectedVisits ?? [];
            const entitiesIds = Array.from(
              new Set(
                visits
                  .map((visit: any) => visit.id)
                  .filter((id: unknown) => id !== null && id !== undefined)
              )
            );
            const caregiverIds = Array.from(
              new Set(
                visits
                  .map((visit: any) => visit.caregiverId)
                  .filter((id: unknown) => id !== null && id !== undefined)
              )
            );
            const scope = this.$rootScope.$new() as any;
            scope.title = "Approve Clock Location";
            scope.okButton = "Approve";
            scope.note = {
              type: "visit_distance_approval",
              noteType: "visit_distance_approval",
              status: "NONE",
              noteRichText: "",
              entitiesIds,
              patientIds: [patientId],
              caregiverIds,
            };

            return (this.$rootScope as any).openNewTextNoteModal(scope).then((result: string) => {
              if (result === "OK") {
                event.payload.onDone?.();
              }
            });
          }
          case "openNewCalendarNote": {
            const scope = this.$rootScope.$new() as any;
            scope.note = {
              patientId,
              calendarDate:
                event.payload.calendarDate === undefined
                  ? undefined
                  : new Date(`${event.payload.calendarDate}T00:00:00.000Z`),
            };

            return (this.$rootScope as any)
              .openNewCalendarNoteModal(scope)
              .then((result: string) => {
                if (result === "OK") {
                  event.payload.onDone?.();
                }
              });
          }
          case "openNewVacation": {
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/new-patient-vacation-modal.html",
              size: "md",
              controller: "newPatientVacationModalCtrl",
              windowClass: "center-center",
              resolve: {
                selectedDate: () =>
                  event.payload.calendarDate === undefined
                    ? new Date()
                    : new Date(`${event.payload.calendarDate}T00:00:00.000Z`),
                patientId: () => patientId,
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openChangeStatus": {
            const scope = this.$rootScope.$new() as any;
            scope.patientId = patientId;
            scope.patient = event.payload.patient ?? { id: patientId };

            const modal = this.$uibModal.open({
              templateUrl: "admin/views/patient-status-change-modal.html",
              controller: "patientStatusChangeModalCtrl",
              scope,
              size: "lg",
            });

            return modal.result.then(() => event.payload.onDone?.());
          }
          case "openAddPhoneNumber": {
            let didComplete = false;
            const handleComplete = () => {
              if (didComplete) {
                return;
              }

              didComplete = true;
              window.dispatchEvent(
                new CustomEvent("from-webapp-react", {
                  detail: {
                    type: "patientProfilePhoneNumbersChange",
                    payload: { patientId },
                  },
                })
              );
              event.payload.onDone?.();
            };
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/new-phonenumber-modal.html",
              controller: "newPhonenumberModalCtrl",
              size: "lg",
              resolve: {
                patient: () => event.payload.patient ?? { id: patientId, phoneNumbers: [] },
                onSuccess: () => () => {
                  handleComplete();
                  modal.close();
                },
                onFail: () => () => undefined,
              },
            });

            return modal.result.then(handleComplete).catch(() => undefined);
          }
        }
      } else if (event.type === "caregiverProfileAction") {
        const caregiverId = event.payload.caregiverId as any;
        const action = event.payload.action as string;

        switch (action) {
          case "openChat":
            if ((this.$rootScope as any).isOnboardingAgency) {
              return this.$rootScope.react.commCenter.openTicketPopup({
                primaryEntity: "Caregiver",
                caregiverId,
              });
            }

            return (this.$rootScope as any).openAgencyChat(caregiverId);
          case "exportProfile":
            return this.exportProfileService.exportCaregiverProfilePDF(
              (this.$rootScope as any).agencyId,
              (this.$rootScope as any).agencyMemberId,
              caregiverId
            );
          case "openExportProfileSettings":
            return this.$uibModal.open({
              templateUrl: "admin/views/export-profile-modal.html",
              size: "md",
              controller: "exportCaregiverProfileModal",
              resolve: {
                agencyId: () => (this.$rootScope as any).agencyId,
                agencyMemberId: () => (this.$rootScope as any).agencyMemberId,
                caregiverId: () => caregiverId,
              },
            });
          case "openNotes":
            return this.entityNotesModalService.handleNotesModalOpen({
              profileId: caregiverId,
              profileName: event.payload.caregiverName,
              notesType: "Caregiver",
            });
          case "openNewCalendarNote": {
            const scope = this.$rootScope.$new() as any;
            scope.note = {
              caregiverId,
              calendarDate:
                event.payload.calendarDate === undefined
                  ? undefined
                  : new Date(`${event.payload.calendarDate}T00:00:00.000Z`),
            };

            return (this.$rootScope as any)
              .openNewCalendarNoteModal(scope)
              .then((result: string) => {
                if (result === "OK") {
                  event.payload.onDone?.();
                }
              });
          }
          case "openNewAbsence": {
            const selectedDate =
              event.payload.calendarDate === undefined
                ? new Date()
                : new Date(`${event.payload.calendarDate}T00:00:00.000Z`);

            const modal = this.$uibModal.open({
              templateUrl: "admin/views/new-caregiver-absence-modal.html",
              size: "md",
              controller: "newCaregiverAbsenceModalCtrl",
              windowClass: "center-center",
              resolve: {
                selectedDate: () => selectedDate,
                caregiverId: () => caregiverId,
              },
            });

            return modal.result.then((result) => {
              if (result === "ABSENCE_CREATED") {
                event.payload.onDone?.();
              }
            });
          }
          case "setCalendarPto": {
            const caregiver = event.payload.caregiver as any;

            if (caregiver?.hireDate === null) {
              window.alert("Caregiver must have hire date in order to add paid time off.");
              return undefined;
            }

            const visits = (event.payload.selectedVisits ?? []) as any[];

            if (visits.length === 0) {
              window.alert("There are no assigned visits in the selected range.");
              return undefined;
            }

            const scope = this.$rootScope.$new() as any;
            scope.caregiverId = caregiverId;
            scope.visitInstances = visits.map((visit) => ({
              id: visit.id,
              visitInstanceId: visit.id,
              patientId: visit.patientId,
              caregiverId,
              titleName: visit.title,
              startTime: visit.startTime,
              endTime: visit.endTime,
              originalStart: moment(String(visit.startTime)),
              originalEnd: moment(String(visit.endTime)),
            }));

            const modal = this.$uibModal.open({
              templateUrl: "admin/views/visit-instance-pto-modal.html",
              size: "md",
              controller: "visitInstancePTOModalCtrl",
              windowTopClass: "PTO-modal",
              scope,
            });

            return modal.result
              .then((result) => {
                if (
                  result === "CREATE_SUCCESS" ||
                  result === "EDIT_SUCCESS" ||
                  result === "REMOVE_SUCCESS"
                ) {
                  this.$rootScope.$broadcast("refresh_ptos");
                  event.payload.onDone?.();
                }
              })
              .catch(() => undefined);
          }
          case "approveCalendarClockLocation": {
            const visits = event.payload.selectedVisits ?? [];
            const entitiesIds = Array.from(
              new Set(
                visits
                  .map((visit: any) => visit.id)
                  .filter((id: unknown) => id !== null && id !== undefined)
              )
            );
            const patientIds = Array.from(
              new Set(
                visits
                  .map((visit: any) => visit.patientId)
                  .filter((id: unknown) => id !== null && id !== undefined)
              )
            );
            const caregiverIds = [caregiverId];
            const scope = this.$rootScope.$new() as any;
            scope.title = "Approve Clock Location";
            scope.okButton = "Approve";
            scope.note = {
              type: "visit_distance_approval",
              noteType: "visit_distance_approval",
              status: "NONE",
              noteRichText: "",
              entitiesIds,
              patientIds,
              caregiverIds,
            };

            return (this.$rootScope as any).openNewTextNoteModal(scope).then((result: string) => {
              if (result === "OK") {
                event.payload.onDone?.();
              }
            });
          }
          case "openChangeStatus": {
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/edit-caregiver-status-modal.html",
              size: "md",
              controller: "editCaregiverStatusModalCtrl",
              resolve: {
                caregiver: () => event.payload.caregiver ?? { id: caregiverId },
                isFromPrioritizedCaregeiversScreen: () => false,
                onStatusChanged: () => () => event.payload.onDone?.(),
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openExclusionLists":
            return this.$uibModal
              .open({
                templateUrl: "admin/views/exclusion-history-modal.html",
                size: "lg",
                controller: "exclusionListModalCtrl",
                resolve: {
                  caregiverId: () => caregiverId,
                },
              })
              .result.catch(() => undefined);
          case "openCaregiverPayRate": {
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/caregiver-pay-rate-modal.html",
              size: "lg",
              controller: "caregiverPayRateModalCtrl",
              resolve: {
                caregiverId: () => caregiverId,
                certifications: () => (event.payload.caregiver as any)?.certifications ?? [],
                caregiverPayRateInitValues: () => event.payload.data,
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openCaregiverPayRateReset": {
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/caregiver-pay-rate-reset-modal.html",
              size: "lg",
              controller: "caregiverPayRateResetModalCtrl",
              resolve: {
                caregiverId: () => caregiverId,
                certifications: () => (event.payload.caregiver as any)?.certifications ?? [],
              },
            });

            return modal.result.then(() => event.payload.onDone?.()).catch(() => undefined);
          }
          case "openCaregiverHoursOverride": {
            const openModal = (allOffices: any[]) => {
              const modal = this.$uibModal.open({
                templateUrl: "admin/views/caregiver-hours-override-modal.html",
                size: "lg",
                controller: "caregiverHoursOverrideModalCtrl",
                resolve: {
                  caregiverId: () => caregiverId,
                  allOffices: () => allOffices,
                  override: () => event.payload.data,
                },
              });

              return modal.result
                .then((override: any) => {
                  const isEdit = override?.id !== undefined && override?.id !== null;
                  const baseUrl = `agencies/${(this.$rootScope as any).agencyId}/agency_member/${
                    (this.$rootScope as any).agencyMemberId
                  }/caregiver/${caregiverId}/hours_overrides`;
                  const request = isEdit
                    ? this.DatabaseApi.put(`${baseUrl}/${override.id}`, override)
                    : this.DatabaseApi.post(baseUrl, override);

                  return request.then(() => event.payload.onDone?.());
                })
                .catch(() => undefined);
            };

            return (this.DatabaseApi as any)
              .get(
                `agencies/${(this.$rootScope as any).agencyId}/agency_members/${
                  (this.$rootScope as any).agencyMemberId
                }/offices`
              )
              .then((res: any) =>
                openModal(
                  (res.data?.offices ?? []).map((office: any) => ({
                    id: office.id,
                    label: office.name,
                  }))
                )
              );
          }
          case "openLegacyCaregiverSection": {
            const numericCaregiverId = Number(caregiverId);
            const legacyCaregiverId = Number.isFinite(numericCaregiverId)
              ? -Math.abs(numericCaregiverId)
              : caregiverId;

            this.$rootScope.openCaregiverModal(
              legacyCaregiverId as any,
              event.payload.caregiver as any,
              true,
              undefined,
              { redirect: false }
            );
            this.activateLegacyCaregiverSection(event);
            return undefined;
          }
          case "addDocumentUpload": {
            const modal = this.$uibModal.open({
              templateUrl: "admin/views/upload-multiple-doc-modal.html",
              size: "md",
              controller: "uploadMultipleDocModalCtrl",
              resolve: {
                docType: () => event.payload.data,
                proId: () => caregiverId,
              },
            });

            return modal.result.finally(() => event.payload.onDone?.());
          }
          case "viewDocumentUpload":
          case "downloadDocumentUpload": {
            const uploadId = (event.payload.data as any)?.uploadId;

            if (uploadId === undefined || uploadId === null) {
              return undefined;
            }

            return this.DatabaseApi.get(
              `agencies/${
                (this.$rootScope as any).agencyId
              }/caregivers/${caregiverId}/document_uploads/${uploadId}/generate_url`
            )
              .then((response: any) => {
                if (event.payload.action === "viewDocumentUpload") {
                  window.open(response.data.fileUrl)?.focus();
                  return;
                }

                const fileUrl = response.data.fileUrl;
                const fileName = String(fileUrl).split("/")[4]?.split("?")[0] ?? "document.pdf";

                return this.$http
                  .get(fileUrl, { responseType: "arraybuffer" })
                  .then((fileResponse) => {
                    const file = new Blob([fileResponse.data as BlobPart], {
                      type: "application/pdf",
                    });
                    const objectUrl = URL.createObjectURL(file);
                    const link = document.createElement("a");

                    link.href = objectUrl;
                    link.download = fileName;
                    link.click();
                    URL.revokeObjectURL(objectUrl);
                  });
              })
              .catch(() => this.toaster.pop("warning", "something went wrong", ""));
          }
          case "sendDocumentUploadToEmail":
          case "sendDocumentUploadToFax": {
            const uploadId = (event.payload.data as any)?.uploadId;

            if (uploadId === undefined || uploadId === null) {
              return undefined;
            }

            return this.DatabaseApi.get(
              `hr/agencies/${
                (this.$rootScope as any).agencyId
              }/caregivers/${caregiverId}/document_file/${uploadId}`
            )
              .then((response: any) =>
                this.$http.get(response.data.url, { responseType: "arraybuffer" }).then(
                  (fileResponse) =>
                    new Promise<void>((resolve) => {
                      const file = new Blob([fileResponse.data as BlobPart], {
                        type: "application/pdf",
                      });
                      const reader = new FileReader();

                      reader.onload = () => {
                        const scope = this.$rootScope.$new() as any;

                        scope.caregiverId = caregiverId;
                        scope.type =
                          event.payload.action === "sendDocumentUploadToEmail" ? "email" : "fax";
                        scope.encodedPdf = reader.result;

                        this.$uibModal.open({
                          templateUrl: "admin/views/caregiver-document-send-pdf-modal.html",
                          size: "md",
                          scope,
                          controller: "caregiverDocumentSendPdfModalCtrl",
                        });
                        resolve();
                      };
                      reader.readAsDataURL(file);
                    })
                )
              )
              .catch(() => this.toaster.pop("warning", "something went wrong", ""));
          }
          case "removeDocumentUpload": {
            const uploadId = (event.payload.data as any)?.uploadId;

            if (uploadId === undefined || uploadId === null) {
              return undefined;
            }

            return this.DatabaseApi.delete(
              `hr/agencies/${
                (this.$rootScope as any).agencyId
              }/caregivers/${caregiverId}/documents/${uploadId}`
            )
              .then(() => {
                this.toaster.pop("success", "Removed successfully");
                event.payload.onDone?.();
              })
              .catch(() => this.toaster.pop("warning", "something went wrong", ""));
          }
          case "unblockLogin":
            return this.DatabaseApi.get(
              "agencies/" +
                (this.$rootScope as any).agencyId +
                "/coordinator/" +
                (this.$rootScope as any).agencyMemberId +
                "/caregivers/" +
                caregiverId +
                "/unblock_login"
            ).then(() => event.payload.onDone?.());
        }
      } else if (event.type === "customAddressChange") {
        switch (event.payload.entity) {
          case "Caregiver":
            this.$rootScope.receiveCaregiverCustomAddressChange(
              event.payload.id as any,
              event.payload.address,
              event.payload.addressComponents,
              event.payload.addressGeoLocation
            );
            break;
          case "Patient":
            this.$rootScope.receivePatientCustomAddressChange(
              event.payload.id as any,
              event.payload.address,
              event.payload.timezone,
              event.payload.addressComponents
            );
            break;
        }
      } else if (event.type === "refreshPatientQuestionTypes") {
        this.DatabaseApi.setPatientQuestionTypes();
      } else if (event.type === "refreshVisits") {
        // Wrapped in $timeout because the browser event always arrives outside
        // the digest — every emitter is a React callback — and the listeners
        // mutate their own scopes, so without it the refetch happens but the
        // view does not repaint until something else triggers a digest. This is
        // the same event masterWeekCtrl broadcasts after creating or editing a
        // master week, so it reaches the weekly template table and both
        // calendars.
        this.$timeout(() => this.$rootScope.$broadcast("refresh_visits"));
      } else if (event.type === "refreshPlansOfCare") {
        // Broadcasts got_plan_of_care_type for the controllers that cache plans of care.
        this.DatabaseApi.getPlansOfCare();
        // PlanOfCareTypeService keeps its own cache, which the new-plan-of-care modal reads
        // without refetching, so it has to be invalidated separately.
        void this.planOfCareTypeService.get({ refetch: true });
      }
    });
  }
}
